import type { SyncErrorCode } from '../types';

/**
 * Copy and behaviour for the `error_code` the sync endpoints report.
 *
 * Two rules the backend contract is explicit about:
 *
 *  - Never show the raw code. `proxy_blocked` means nothing to a seller.
 *  - Never show `message`/`last_sync_error` verbatim when a code is present.
 *    Those strings are written for us, not for the user — the proxy ones embed
 *    the account's phone number mid-sentence.
 *
 * `needs_reconnect` stays the only source for the reconnect prompt. Nothing in
 * here feeds it: these codes describe the last ticket sync, not auth state, and
 * the two diverge in both directions.
 */

/**
 * Codes that mean our proxy had a bad minute, not that anything is wrong with
 * the seller's account.
 *
 * Retrying is worth offering: by the time we see either code the backend has
 * tried three exit nodes and recorded that it moved on from them, so the next
 * attempt lands on three different ones. It used to re-use the same three,
 * which is why an account could fail forever no matter how often Sync was hit.
 */
const RETRYABLE_SYNC_ERROR_CODES: SyncErrorCode[] = ['proxy_blocked', 'network'];

export function isRetryableSyncError(code?: SyncErrorCode | null): boolean {
  return !!code && RETRYABLE_SYNC_ERROR_CODES.includes(code);
}

const SYNC_ERROR_COPY: Record<SyncErrorCode, string> = {
  proxy_blocked: 'Temporarily unavailable — try again',
  network: 'Temporarily unavailable — try again',
  banned: 'DICE has blocked this account. Reconnecting will not fix it — contact support.',
  token_expired: 'Session expired during sync',
  refresh_failed: 'Could not refresh this account',
  forbidden: 'Failed to sync account',
  unknown: 'Failed to sync account',
};

/**
 * User-facing copy for a sync failure. `fallback` is only reached when the
 * server sent no code at all — an older build, or a failure that never got as
 * far as the sync classifier.
 */
export function syncErrorMessage(
  code?: SyncErrorCode | null,
  fallback = 'Failed to sync account',
): string {
  return (code && SYNC_ERROR_COPY[code]) || fallback;
}

/**
 * A sync that came back with a failure body. Thrown by `api.syncAccount` so a
 * failed sync rejects rather than resolving — the HTTP status alone can't be
 * branched on any more (`proxy_blocked` is 503, `network` and `forbidden` 400,
 * `banned` and the auth codes 401).
 *
 * `message` is already user-safe copy; `serverMessage` is the raw string for
 * logs.
 */
export class SyncFailedError extends Error {
  readonly errorCode: SyncErrorCode | null;
  readonly status: number;
  readonly serverMessage?: string;

  constructor(errorCode: SyncErrorCode | null, serverMessage: string | undefined, status: number) {
    super(syncErrorMessage(errorCode));
    this.name = 'SyncFailedError';
    this.errorCode = errorCode;
    this.status = status;
    this.serverMessage = serverMessage;
  }

  get isRetryable(): boolean {
    return isRetryableSyncError(this.errorCode);
  }
}
